import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polyline, CircleMarker, Tooltip, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const BUS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 6v6"/><path d="M15 6v6"/><path d="M2 12h19.6"/><path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3"/><circle cx="7" cy="18" r="2"/><path d="M9 18h5"/><circle cx="16" cy="18" r="2"/></svg>';

const busIcon = (b, on) => L.divIcon({
  className: "", iconSize: [38, 38], iconAnchor: [19, 19],
  html: `<div data-testid="bus-marker-${b.trip_id}" style="width:38px;height:38px;border-radius:50%;display:grid;place-items:center;border:3px solid white;box-shadow:0 6px 16px rgba(11,19,36,.35);background:${b.delay_min > 10 ? "#E11D48" : on ? "#0052FF" : "#182678"};transform:scale(${on ? 1.15 : 1})">${BUS_SVG}</div>`,
});

const coords = (tl) => tl.filter((s) => s.lat != null).map((s) => [s.lat, s.lng]);

function Fit({ points, sig }) {
  const map = useMap();
  useEffect(() => { if (points.length) map.fitBounds(points, { padding: [40, 40], maxZoom: 9 }); }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function FlyTo({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (!pos) return undefined;
    const id = requestAnimationFrame(() => { try { map.stop(); map.flyTo(pos, Math.max(map.getZoom(), 8), { duration: 0.8 }); } catch { /* map not ready */ } });
    return () => cancelAnimationFrame(id);
  }, [pos?.[0], pos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LiveMap({ buses, selected, onSelect, renderPopup, geometries = {}, className = "h-[520px]" }) {
  const stations = {};
  buses.forEach((b) => b.timeline.forEach((s) => { if (s.lat != null) stations[s.station_id] = s; }));
  const sel = buses.find((b) => b.trip_id === selected);
  const pts = buses.flatMap((b) => coords(b.timeline));
  return (
    <div className={`relative z-0 overflow-hidden rounded-3xl border border-slate-200 bg-slate-100 ${className}`} data-testid="live-map">
      <MapContainer center={[7.9, -1.0]} zoom={7} className="h-full w-full" scrollWheelZoom>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Fit points={pts} sig={buses.map((b) => b.trip_id).join()} />
        {sel?.position?.lat != null && sel?.position?.lng != null && <FlyTo pos={[sel.position.lat, sel.position.lng]} />}
        {buses.map((b) => {
          const road = geometries[b.trip_id];
          const line = road && road.length > 1 ? road : coords(b.timeline);
          return <Polyline key={`l-${b.trip_id}`} positions={line} pathOptions={{ color: b.trip_id === selected ? "#0052FF" : "#94A3B8", weight: b.trip_id === selected ? 5 : 3, dashArray: road && road.length > 1 ? undefined : "8 8" }} />;
        })}
        {Object.values(stations).map((s) => (
          <CircleMarker key={s.station_id} center={[s.lat, s.lng]} radius={6} pathOptions={{ color: "#182678", fillColor: "#FCD116", fillOpacity: 1, weight: 2 }}>
            <Tooltip direction="bottom" offset={[0, 6]}>{s.city} station</Tooltip>
          </CircleMarker>
        ))}
        {buses.filter((b) => b.position && b.position.lat != null && b.position.lng != null).map((b) => (
          <Marker key={b.trip_id} position={[b.position.lat, b.position.lng]} icon={busIcon(b, b.trip_id === selected)} eventHandlers={{ click: () => onSelect?.(b) }}>
            <Tooltip direction="top" offset={[0, -20]}>{b.bus_number} · {b.route}</Tooltip>
            {renderPopup && <Popup minWidth={260}>{renderPopup(b)}</Popup>}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
